export type ApiEnvironment = 'production' | 'staging' | 'local' | 'custom';
export interface ApiEndpoints {
  lawvoApi: string;
  dcApi: string;
}
const ENV_STORAGE_KEY = 'lawvo_group_setup_api_environment';
const CUSTOM_ENDPOINTS_KEY = 'lawvo_group_setup_custom_api_endpoints';

/**
 * Safely read Vite-style env vars. `import.meta.env` is only populated under a
 * Vite build; in other runtimes it may be undefined, so guard every access.
 */
function readEnv(): Record<string, string | boolean | undefined> {
  try {
    const meta = import.meta as unknown as {
      env?: Record<string, string | boolean | undefined>;
    };
    return meta.env ?? {};
  } catch {
    return {};
  }
}
const ENV = readEnv();
export const API_ENV_PRESETS: Record<Exclude<ApiEnvironment, 'custom'>, ApiEndpoints> = {
  production: {
    lawvoApi: 'https://api.lawvo.com',
    dcApi: 'https://dcapi.lawvo.com'
  },
  staging: {
    lawvoApi: ENV.VITE_STAGING_LAWVO_API as string || 'https://staging-api.lawvo.com',
    dcApi: ENV.VITE_STAGING_DC_API as string || 'https://staging-dcapi.lawvo.com'
  },
  local: {
    lawvoApi: ENV.VITE_LOCAL_LAWVO_API as string || 'http://localhost:3030',
    dcApi: ENV.VITE_LOCAL_DC_API as string || 'http://localhost:1367'
  }
};
function readStoredEnvironment(): ApiEnvironment {
  try {
    const stored = localStorage.getItem(ENV_STORAGE_KEY);
    if (stored === 'production' || stored === 'staging' || stored === 'local' || stored === 'custom') {
      return stored;
    }
  } catch {

    // ignore
  }
  const viteDefault = ENV.VITE_API_ENVIRONMENT;
  if (viteDefault === 'production' || viteDefault === 'staging' || viteDefault === 'local' || viteDefault === 'custom') {
    return viteDefault;
  }
  return ENV.DEV ? 'local' : 'production';
}
function readCustomEndpoints(): ApiEndpoints | null {
  try {
    const stored = localStorage.getItem(CUSTOM_ENDPOINTS_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (parsed?.lawvoApi && parsed?.dcApi) return parsed;
  } catch {

    // ignore
  }
  return null;
}
export function getApiEndpoints(): ApiEndpoints {
  const env = readStoredEnvironment();
  if (env === 'custom') {
    return readCustomEndpoints() || {
      lawvoApi: ENV.VITE_LAWVO_API as string || API_ENV_PRESETS.production.lawvoApi,
      dcApi: ENV.VITE_DC_API as string || API_ENV_PRESETS.production.dcApi
    };
  }
  return API_ENV_PRESETS[env];
}
export function getLawvoApiBase(): string {
  return getApiEndpoints().lawvoApi.replace(/\/$/, '');
}
export function getDcApiBase(): string {
  return getApiEndpoints().dcApi.replace(/\/$/, '');
}
export function getSubmitWebhookUrl(): string {
  return ENV.VITE_SUBMIT_WEBHOOK_URL as string || 'https://lab.lawvo.com/webhook/onboarding-questioner';
}

/** Matches lawvo-client REACT_APP_HASH_SECRET_KEY default. */
export function getHookSecretKey(): string {
  return ENV.VITE_HASH_SECRET_KEY as string || '370c241b361110170611113418001a40554640';
}

/** When true, list/find responses from lawvo-api have encrypted `data` fields. */
export function isHashDataEnabled(): boolean {
  const raw = ENV.VITE_ENABLE_HASH_DATA;
  if (raw === 'false' || raw === '0') return false;
  if (raw === 'true' || raw === '1') return true;
  return true;
}